import type {
  FocusEventHandler,
  ChangeEventHandler,
  KeyboardEventHandler,
  ReactNode,
} from "react"
import {
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react"
import { copyValidityState } from "./copyValidityState"
import { defaultValidityState } from "./defaultValidityState"
import { ValidationContext } from "./ValidationContext"
import type { ValidationElement } from "./ValidationElement"
import { ValidationMode } from "./ValidationMode"

interface Props {
  validationMode?: ValidationMode[keyof ValidationMode];
  children?: ReactNode;
}

export function ValidationContextProvider(props: Props) {
  const {
    validationMode = ValidationMode.OnFirstInteraction,
    children,
  } = props

  const elementRef = useRef<ValidationElement>()

  const [
    hasBlurred,
    setHasBlurred,
  ] = useState<boolean>(false)

  const [
    hasSubmitted,
    setHasSubmitted,
  ] = useState<boolean>(false)

  const [
    hasChanged,
    setHasChanged,
  ] = useState<boolean>(false)

  const [
    validityState,
    setValidityState,
  ] = useState<ValidityState>(defaultValidityState)

  // a reference to the validation element
  const ref = useCallback(
    (element: ValidationElement | null) => {
      if (element) {
        setValidityState(copyValidityState(element.validity))
        elementRef.current = element
      }
    },
    [
      setValidityState,
      elementRef,
    ],
  )

  // onBlur handler for the validation element
  const onBlur: FocusEventHandler<ValidationElement> = useCallback(
    () => {
      setHasBlurred(true)
    },
    [setHasBlurred],
  )

  // ref handler for the validation element
  const refCallback = useCallback(
    (element: ValidationElement | null) => {
      if (element) {
        setValidityState(copyValidityState(element.validity))
      }
    },
    [setValidityState],
  )

  // onChange handler for the validation element
  const onChange: ChangeEventHandler<ValidationElement> = useCallback(
    (e) => {
      setValidityState(copyValidityState(e.target.validity))
      setHasChanged(true)
    },
    [
      setValidityState,
      setHasChanged,
    ],
  )

  // onChange handler for the validation element
  const onControlChange = useCallback(
    () => {
      if (elementRef.current) {
        setValidityState(copyValidityState(elementRef.current.validity))
        setHasChanged(true)
      }
    },
    [
      elementRef,
      setValidityState,
      setHasChanged,
    ],
  )

  // onKeyDown handler for the validation element
  const onKeyDown: KeyboardEventHandler<ValidationElement> = useCallback(
    (e) => {
      if (e.code === "Enter" || e.code === "NumpadEnter") {
        setHasSubmitted(true)
      }
    },
    [setHasSubmitted],
  )

  const showValidationMessage = useMemo(
    () => {
      if (validationMode === ValidationMode.Immediate) {
        return true
      }

      if (!elementRef.current) {
        return hasBlurred
      }

      if (elementRef.current instanceof HTMLSelectElement) {
        return hasChanged || hasBlurred
      }

      if (elementRef.current instanceof HTMLInputElement) {
        return hasSubmitted || hasBlurred
      }

      return hasBlurred
    },
    [
      hasBlurred,
      hasChanged,
      hasSubmitted,
      validationMode,
      elementRef,
    ],
  )

  const value = useMemo(
    () => ({
      validityState,
      showValidationMessage,
      validationElementProps: {
        ref,
        onChange,
        onBlur,
        onKeyDown,
        refCallback,
        onControlChange,
      },
    }),
    [
      validityState,
      showValidationMessage,
      ref,
      onChange,
      onBlur,
      onKeyDown,
      refCallback,
      onControlChange,
    ],
  )

  return (
    <ValidationContext.Provider value={value}>
      {children}
    </ValidationContext.Provider>
  )
}
